import {useState} from "react";
import {useRecordEvent} from "aws-rum-react";
import {Storage} from "aws-amplify";
import {useConfirmNavigation} from "./useConfirmNavigation";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const useFileUpload = () => {
    const recordEvent = useRecordEvent();
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [fileMap, setFileMap] = useState<Record<string, string>>({});
    const [isUploadCompleted, setIsUploadCompleted] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const {
        isModalOpen,
        confirmNavigation,
        cancelNavigation,
        setIsDirty,
        setIsModalOpen
    } = useConfirmNavigation();

    const handleUploadFiles = async (files: File[]) => {
        recordEvent("StartUploadingPdfEvent", {
            description: "Extraction of special tools for uploaded PDF files was started",
        });

        setIsUploading(true);
        const fileProgress = new Array(files.length).fill(0);

        try {
            const uploadPromises = files.map((file, index) =>
                uploadFile(file, index, fileProgress.slice(), files.length)
            );

            await Promise.all(uploadPromises);

            setProgress(100);
            setIsUploadCompleted(true);
            setIsDirty(true);
        } catch (error) {
            console.error("File upload failed:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const uploadFile = async (
        file: File,
        index: number,
        fileProgress: number[],
        totalFiles: number,
        retries: number = MAX_RETRIES
    ): Promise<string> => {
        const key = `special_tools_extraction/pdf_files/${file.name}`;

        const updateProgress = (loaded: number, total: number) => {
            fileProgress[index] = total > 0 ? loaded / total : 0;
            const totalProgress =
                (fileProgress.reduce((acc, curr) => acc + curr, 0) / totalFiles) * 100;
            setProgress(totalProgress);
        };

        try {
            const result = await Storage.put(key, file, {
                contentType: file.type,
                customPrefix: {public: ""},
                progressCallback: ({loaded, total}) => updateProgress(loaded, total),
            });

            const path = result.key;
            const properties = await Storage.getProperties(path, {customPrefix: {public: ""}});

            const fileName = key.split("/").pop() ?? file.name;
            const eTag = properties.eTag?.slice(1, -1) ?? "";

            setFileMap((prev) => ({...prev, [fileName]: eTag}));
            setUploadedFiles((prev) => [...prev, file]);

            return path;
        } catch (error) {
            if (retries > 0) {
                console.warn(`Upload failed. Retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
                await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
                return uploadFile(file, index, fileProgress, totalFiles, retries - 1);
            }
            console.error("Upload failed after maximum retries:", error);
            throw error;
        }
    };

    const handleClearUploadedFile = () => {
        setUploadedFiles([]);
        setFileMap({});
        setIsUploadCompleted(false);
        setProgress(0);
    };

    return {
        progress,
        isUploading,
        handleUploadFiles,
        handleClearUploadedFile,
        fileMap,
        isUploadCompleted,
        uploadedFiles,
        isModalOpen, confirmNavigation, cancelNavigation, setIsDirty, setIsModalOpen
    };
};
